import React, { useState, useEffect, useRef } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { ShoppingCart, Search, X, Menu } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { products } from '../utils/productsData';
import '../styles/Navbar.css';
import logoImg from '../assets/logos/BMC LOGO.PNG';

const Navbar = () => {
  const [showSearch, setShowSearch] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const searchInputRef = useRef(null);
  const suggestionsRef = useRef(null);
  const mobileMenuRef = useRef(null);
  const navigate = useNavigate();
  const { cartCount } = useCart();

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  const toggleSearch = () => {
    if (showSearch) {
      setIsClosing(true);
      setTimeout(() => {
        setShowSearch(false);
        setIsClosing(false);
      }, 300);
    } else {
      setShowSearch(true);
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    }
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    
    if (value.trim().length > 0) {
      generateSuggestions(value.trim());
      setShowSuggestions(true);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const generateSuggestions = (query) => {
    const queryLower = query.toLowerCase();
    const productSuggestions = [];
    const categorySuggestions = [];
    
    // Ensure products exist
    const safeProducts = products || [];
    const categories = [...new Set(safeProducts.map(p => p.category))];
    
    const searchMappings = {
      'lens': ['Lens'], 'lenses': ['Lens'],
      'camera': ['Camera'], 'cameras': ['Camera'],
      'light': ['Lights'], 'lights': ['Lights'],
      'gimbal': ['Gimbal'], 'audio': ['Audio'],
      'tripod': ['Tripod']
    };
    
    categories.forEach(category => {
      if (category && category.toLowerCase().includes(queryLower)) {
        categorySuggestions.push({
          type: 'category',
          name: category,
          count: safeProducts.filter(p => p.category === category).length
        });
      }
    });
    
    if (searchMappings[queryLower]) {
      searchMappings[queryLower].forEach(categoryName => {
        if (!categorySuggestions.some(cat => cat.name === categoryName)) {
          categorySuggestions.push({
            type: 'category',
            name: categoryName,
            count: safeProducts.filter(p => p.category === categoryName).length
          });
        }
      });
    }
    
    safeProducts.forEach(product => {
      if (!product.name) return;
      
      if (product.name.toLowerCase().includes(queryLower)) {
        productSuggestions.push({
          type: 'product',
          name: product.name,
          category: product.category,
          id: product.id,
          image: product.image
        });
      }
    });
    
    setSuggestions([
      ...categorySuggestions.slice(0, 2),
      ...productSuggestions.slice(0, 5)
    ]);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigateToSearch(searchQuery.trim());
    }
  };

  const handleSuggestionClick = (suggestion) => {
    if (suggestion.type === 'product') {
      navigate(`/products?productId=${suggestion.id}`);
    } else if (suggestion.type === 'category') {
      navigate(`/products?category=${encodeURIComponent(suggestion.name)}`);
    } else {
      navigateToSearch(suggestion.name);
    }
    closeSearch();
  };

  const navigateToSearch = (query) => {
    navigate(`/products?search=${encodeURIComponent(query)}`);
    closeSearch();
  };

  const closeSearch = () => {
    setIsClosing(true);
    setTimeout(() => {
      setShowSearch(false);
      setIsClosing(false);
      setSearchQuery('');
      setSuggestions([]);
      setShowSuggestions(false);
    }, 300);
  };

  // Close on click outside (search & mobile menu)
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showSearch && !event.target.closest('.navbar-search-container')) {
         closeSearch();
      }
      if (isMobileMenuOpen && mobileMenuRef.current && !mobileMenuRef.current.contains(event.target) && !event.target.closest('.hamburger-menu-btn')) {
        setIsMobileMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showSearch, isMobileMenuOpen]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  // Close mobile menu when resizing to desktop size
  useEffect(() => {
    const handleResize = () => {
      // Close mobile menu if window width is above hamburger breakpoint (1180px)
      if (window.innerWidth > 1180 && isMobileMenuOpen) {
        setIsMobileMenuOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    
    // Also check on mount
    handleResize();
    
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [isMobileMenuOpen]);

  return (
    <nav className="navbar">
      <div className="navbar-container">
        
        {/* Hamburger Menu Button (Mobile) */}
        <button 
          className="hamburger-menu-btn"
          onClick={toggleMobileMenu}
          aria-label="Toggle menu"
        >
          {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
        
        {/* 1. Brand Logo */}
        <div className="navbar-brand">
          <NavLink to="/" className="navbar-logo-link" onClick={closeMobileMenu}>
            <div className="navbar-logo">
              <img src={logoImg} alt="BookMyCamera" />
              <span className="navbar-logo-text">BookMyCamera</span>
            </div>
          </NavLink>
        </div>
        
        {/* 2. Navigation Links (Desktop) */}
        <div className={`navbar-links ${showSearch ? 'hidden' : ''}`}>
          <NavLink to="/" className={({isActive}) => isActive ? "nav-link active" : "nav-link"} onClick={closeMobileMenu}>Home</NavLink>
          <NavLink to="/event-packages" className={({isActive}) => isActive ? "nav-link active" : "nav-link"} onClick={closeMobileMenu}>Packages</NavLink>
          <NavLink to="/products" className={({isActive}) => isActive ? "nav-link active" : "nav-link"} onClick={closeMobileMenu}>Rental Gears</NavLink>
          <NavLink to="/services" className={({isActive}) => isActive ? "nav-link active" : "nav-link"} onClick={closeMobileMenu}>Services</NavLink>
          <NavLink to="/contact" className={({isActive}) => isActive ? "nav-link active" : "nav-link"} onClick={closeMobileMenu}>Contact</NavLink>
          <NavLink to="/about" className={({isActive}) => isActive ? "nav-link active" : "nav-link"} onClick={closeMobileMenu}>About</NavLink>
        </div>
        
        {/* 3. Actions: Search, Cart */}
        <div className="navbar-actions">
          
          {/* Search */}
          <div className="navbar-search-container">
            <button className="navbar-search-icon-btn" onClick={toggleSearch}>
               {showSearch ? <X size={20} /> : <Search size={20} />}
            </button>
            
            {showSearch && (
              <>
                <div className={`navbar-search-bar ${isClosing ? 'closing' : ''}`}>
                  <form onSubmit={handleSearchSubmit}>
                    <input
                      ref={searchInputRef}
                      type="text"
                      placeholder="Search camera gear..."
                      value={searchQuery}
                      onChange={handleSearchChange}
                      className="navbar-search-input"
                    />
                  </form>
                </div>
                {/* Suggestions Dropdown */}
                {suggestions.length > 0 && showSuggestions && (
                  <div ref={suggestionsRef} className="navbar-search-suggestions">
                    {suggestions.map((suggestion, index) => (
                      <div
                        key={index}
                        className="navbar-suggestion-item"
                        onClick={() => handleSuggestionClick(suggestion)}
                      >
                        {suggestion.type === 'product' && (
                          <div className="navbar-suggestion-image">
                            <img src={suggestion.image} alt={suggestion.name} />
                          </div>
                        )}
                        <div className="navbar-suggestion-content">
                          <span className="navbar-suggestion-name">{suggestion.name}</span>
                          {suggestion.type === 'category' && (
                            <span className="navbar-suggestion-count">{suggestion.count} items</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
          
          {/* Cart & Likes */}
          <NavLink to="/cart" className="cart-icon">
            <ShoppingCart size={22} />
            {cartCount > 0 && <span className="badge cart-badge">{cartCount}</span>}
          </NavLink>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div className="mobile-menu-overlay" onClick={closeMobileMenu}>
          <div className="mobile-menu" ref={mobileMenuRef} onClick={(e) => e.stopPropagation()}>
            <div className="mobile-menu-header">
              <h3>Menu</h3>
              <button className="mobile-menu-close" onClick={closeMobileMenu}>
                <X size={24} />
              </button>
            </div>
            <div className="mobile-menu-links">
              <NavLink to="/" className={({isActive}) => isActive ? "mobile-nav-link active" : "mobile-nav-link"} onClick={closeMobileMenu}>Home</NavLink>
              <NavLink to="/event-packages" className={({isActive}) => isActive ? "mobile-nav-link active" : "mobile-nav-link"} onClick={closeMobileMenu}>Packages</NavLink>
              <NavLink to="/products" className={({isActive}) => isActive ? "mobile-nav-link active" : "mobile-nav-link"} onClick={closeMobileMenu}>Rental Gears</NavLink>
              <NavLink to="/services" className={({isActive}) => isActive ? "mobile-nav-link active" : "mobile-nav-link"} onClick={closeMobileMenu}>Services</NavLink>
              <NavLink to="/contact" className={({isActive}) => isActive ? "mobile-nav-link active" : "mobile-nav-link"} onClick={closeMobileMenu}>Contact</NavLink>
              <NavLink to="/about" className={({isActive}) => isActive ? "mobile-nav-link active" : "mobile-nav-link"} onClick={closeMobileMenu}>About</NavLink>
            </div>
            <div className="mobile-menu-actions">
              <NavLink to="/cart" className="mobile-action-link" onClick={closeMobileMenu}>
                <ShoppingCart size={20} />
                <span>Cart {cartCount > 0 && `(${cartCount})`}</span>
              </NavLink>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
